/**
 * Event Editor Constants
 * 
 * Centralized constants for event editor components to avoid hardcoded values
 * and ensure consistency across the application.
 */

// Default Values
export const EVENT_EDITOR_DEFAULTS = {
  TYPE_OF_CONTACT: 'phone' as const,
  STATUS_ID: 1 as const,
  EDITOR_HEIGHT: '320px' as const,
  DEFAULT_DURATION_HOURS: 1 as const,
} as const

// Modal Dismissal Types
export const MODAL_DISMISSAL_TYPES = {
  CANCEL: 'cancel' as const,
  CREATE: 'create' as const,
  UPDATE: 'update' as const,
  DELETE: 'delete' as const,
} as const

// Form Field Names
export const FORM_FIELDS = {
  TYPE_OF_CONTACT: 'typeOfContact' as const,
  TITLE: 'title' as const,
  DESCRIPTION: 'description' as const,
  PLANNED_FROM: 'plannedFrom' as const,
  PLANNED_TO: 'plannedTo' as const,
  REALIZED_FROM: 'realizedFrom' as const,
  REALIZED_TO: 'realizedTo' as const,
  STATUS_ID: 'statusId' as const,
  ALLOCATION_ID: 'allocationId' as const,
  EVENT_TYPE_ID: 'eventTypeId' as const,
} as const

// Contact Types
export const CONTACT_TYPES = {
  PHONE: 'phone' as const,
  EMAIL: 'email' as const,
  IN_PERSON: 'in_person' as const,
  VIDEO_CALL: 'video_call' as const,
} as const

// CSS Classes
export const CSS_CLASSES = {
  SECONDARY: 'secondary' as const,
  DANGER: 'danger' as const,
  PRIMARY: 'primary' as const,
} as const

// Translation Keys
export const TRANSLATION_KEYS = {
  CREATE: 'EVENT_EDITOR.CREATE' as const,
  UPDATE: 'EVENT_EDITOR.UPDATE' as const,
  DELETE_CONFIRMATION_HEADER: 'EVENT_EDITOR.DELETE_CONFIRMATION_HEADER' as const,
  DELETE_CONFIRMATION_MESSAGE: 'EVENT_EDITOR.DELETE_CONFIRMATION_MESSAGE' as const,
  CANCEL: 'COMMON.CANCEL' as const,
  DELETE: 'COMMON.DELETE' as const,
  EVENT_SAVED_SUCCESS: 'COMMON.TOAST.EVENT_SAVED_SUCCESS' as const,
  EVENT_DELETED_SUCCESS: 'COMMON.TOAST.EVENT_DELETED_SUCCESS' as const,
  EVENT_SAVE_ERROR: 'COMMON.TOAST.EVENT_SAVE_ERROR' as const,
  EVENT_DELETE_ERROR: 'COMMON.TOAST.EVENT_DELETE_ERROR' as const,
  SELECT_CLIENTS: 'EVENT_EDITOR.SELECT_CLIENTS' as const,
  MAX_CLIENTS_SELECTED: 'EVENT_EDITOR.MAX_CLIENTS_SELECTED' as const,
  ADD_MORE_CLIENTS: 'EVENT_EDITOR.ADD_MORE_CLIENTS' as const,
  UNKNOWN_CLIENT: 'EVENT_EDITOR.UNKNOWN_CLIENT' as const,
  NO_CLIENT_SELECTED: 'EVENT_EDITOR.NO_CLIENT_SELECTED' as const,
  CLIENTS_SELECTED: 'EVENT_EDITOR.CLIENTS_SELECTED' as const,
  WORKER_SELECT_ERROR_LOADING: 'EVENT_EDITOR.WORKER_SELECT.ERROR_LOADING' as const,
} as const

// Validation Messages
export const VALIDATION_MESSAGES = {
  REQUIRED_CONFIG: 'BaseEventEditorComponent requires config input' as const,
  REQUIRED_EVENT_ID: 'Event ID is required for deletion' as const,
  DELETE_NOT_ALLOWED: 'Delete not allowed for this operation or missing event ID' as const,
} as const

// Debug and Warning Messages
export const DEBUG_MESSAGES = {
  PREVENTING_DOUBLE_INIT: 'BaseEventEditorComponent: Preventing double initialization' as const,
  FAILED_TO_FETCH_STATUSES: 'Failed to fetch statuses:' as const,
  FAILED_TO_DELETE_EVENT: 'Failed to delete event:' as const,
  FEATHERS_REQUEST_FAILED: 'Feathers request failed:' as const,
  FAILED_TO_LOAD_WORKERS: 'Error loading workers in modal:' as const,
  SELECTABLE_COMPONENT_NOT_AVAILABLE: 'SelectableComponent not available' as const,
} as const

// Internal System Constants
export const STATE_MANAGEMENT_KEYS = {
  STATE_UPDATE: 'stateUpdate' as const,
  USER_ACTION: 'userAction' as const,
  INITIALIZATION: 'initialization' as const,
} as const

// Event State Property Keys
export const EVENT_STATE_KEYS = {
  BASE: 'base' as const,
  SR_EVENT: 'sr-event' as const,
  ASSISTANCE_EVENT_PARAMS: 'assistanceEvent' as const,
} as const

export const COMPONENT_DEFAULTS = {
  DEFAULT_EVENT_TYPE_ID: 123 as const,
} as const

// Default Values and Fallbacks
export const DEFAULT_VALUES = {
  EMPTY_STRING: '' as const,
  LOADING_TEXT: 'COMMON.LOADING' as const, // Transloco key
  DEFAULT_EVENT_TYPE: 'EVENT_EDITOR.DEFAULT_EVENT_TYPE' as const, // Generic fallback
} as const

// Search Configuration
export const SEARCH_CONFIG = {
  EMPTY_SEARCH_TERM: '' as const,
  CLIENT_SEARCH_FIELDS: ['user.firstName', 'user.lastName'],
} as const

// Worker Name Parsing
export const WORKER_NAME_CONFIG = {
  NAME_SEPARATOR: ' ' as const,
  FIRST_NAME_INDEX: 0 as const,
  LAST_NAME_START_INDEX: 1 as const,
} as const

// Component Properties
export const COMPONENT_PROPERTIES = {
  SET_BASE_EVENT_EDITOR: 'setBaseEventEditor' as const,
} as const
